import { createFileRoute, useNavigate, useRouter, useRouterState } from '@tanstack/react-router'
import maplibregl from 'maplibre-gl'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import { MapRenderContext } from 'src/contexts/MapRenderContext'
import { useNavigatorContext } from 'src/contexts/NavigatorContext'
import NavigatorLegCard from 'src/components/plan/NavigatorLegCard'
import { Plan } from 'src/generated/brezavta/requests/types.gen'
import { getColorForMode } from 'src/lib/modes'
import { resetAllNotifications } from 'src/lib/navigatorNotifications'
import { useLayerData, darkenColor } from 'src/lib/util'
import { TRANSIT_MODES, fitCurrentLegs } from 'src/lib/navigationUtils'
import { DateTime } from 'luxon'

export const Route = createFileRoute('/plan/navigate')({
    component: NavigatorRoute,
})

function NavigatorRoute() {
    const { t } = useTranslation()
    const routerState = useRouterState()
    const navigate = useNavigate()
    const router = useRouter()
    const { navState, startNavigation, stopNavigation, startSimulation, stopSimulation, updateLegIndex, switchToAlternative } = useNavigatorContext()
    const { map, mapLoaded } = useContext(MapRenderContext)

    const markerRef = useRef<maplibregl.Marker | null>(null)
    const prevLegRef = useRef(-1)

    const statePlan = (routerState.location.state as unknown as { plan?: Plan })?.plan as Plan | undefined
    useEffect(() => {
        if (statePlan) {
            startNavigation(statePlan)
        }
        else if (!navState?.plan) {
            navigate({ to: '/plan' } as Record<string, unknown>)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const plan = navState?.plan ?? statePlan
    const legIndex = navState?.legIndex ?? 0
    const currentLeg = plan?.legs[legIndex]
    const nextStop = navState?.nextStop ?? null
    const transferStatus = navState?.transferStatus
    const delaySeconds = navState?.delaySeconds
    const currentTransfer = navState?.currentTransfer
    const nextWalkStep = navState?.nextWalkStep ?? null

    // ── User position marker ─────────────────────────────────────────────────

    useEffect(() => {
        if (!mapLoaded || !map?.current) return
        const el = document.createElement('div')
        Object.assign(el.style, {
            width: '18px', height: '18px', borderRadius: '50%',
            background: '#4285f4', border: '3px solid white',
            boxShadow: '0 2px 8px rgba(0,0,0,.4)', boxSizing: 'border-box',
        })
        const initPos = navState?.userPos
        markerRef.current = new maplibregl.Marker({ element: el })
            .setLngLat(initPos ? [initPos.lon, initPos.lat] : [14.505751, 46.056946])
            .addTo(map.current)
        return () => {
            markerRef.current?.remove()
            markerRef.current = null
        }
    }, [mapLoaded, map]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (navState?.userPos && markerRef.current) {
            markerRef.current.setLngLat([navState.userPos.lon, navState.userPos.lat])
        }
    }, [navState?.userPos])

    // ── Follow user position ─────────────────────────────────────────────────

    useEffect(() => {
        if (!navState?.userPos || !map?.current) return
        map.current.easeTo({
            center: [navState.userPos.lon, navState.userPos.lat],
            duration: 400,
        })
    }, [navState?.userPos]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Map fit on leg change ────────────────────────────────────────────────

    useEffect(() => {
        if (!mapLoaded || !plan || !map?.current) return
        if (legIndex === prevLegRef.current) return
        prevLegRef.current = legIndex
        fitCurrentLegs(map.current, plan, legIndex)
    }, [legIndex, mapLoaded, plan, map])

    useEffect(() => {
        if (!mapLoaded || !plan || !map?.current) return
        fitCurrentLegs(map.current, plan, legIndex)
    }, [mapLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Route layer ──────────────────────────────────────────────────────────

    const layerData = useMemo(() => plan && ({
        type: 'FeatureCollection',
        features: plan.legs.filter(l => l.geometry).map((leg, i) => ({
            type: 'Feature',
            geometry: leg.geometry,
            properties: { color: i < legIndex ? '#cccccc' : getColorForMode(leg), casingColor: darkenColor(i < legIndex ? '#cccccc' : getColorForMode(leg), 0.45) },
        })),
    }), [plan, legIndex])

    useLayerData('planView', layerData, map, mapLoaded)

    // ── Render ───────────────────────────────────────────────────────────────

    if (!plan || !currentLeg) return null

    const destination = plan.legs[plan.legs.length - 1].to_point

    const handleReplan = () => {
        const pos = navState?.userPos
        const stateLegIndex = navState?.legIndex ?? 0
        const leg = plan.legs[stateLegIndex]
        if (!pos && !leg) return

        let fromLat: number, fromLon: number, fromTitle: string, departTime: string

        if (leg && TRANSIT_MODES.includes(leg.mode) && navState?.nextStop) {
            const nextStopData = leg.intermediate_stops?.find(s => s.gtfs_id === navState.nextStop!.gtfs_id)
            if (nextStopData) {
                fromLat = nextStopData.lat
                fromLon = nextStopData.lon
                fromTitle = nextStopData.name
                const depTime = nextStopData.departure_time
                    ? nextStopData.departure_time - 60_000
                    : Date.now()
                departTime = DateTime.fromMillis(depTime, { zone: 'Europe/Ljubljana' }).toFormat('yyyy-MM-dd\'T\'HH:mm')
            }
            else {
                fromLat = pos?.lat ?? leg.from_point.lat
                fromLon = pos?.lon ?? leg.from_point.lon
                fromTitle = pos ? t('Current location') : leg.from_point.name
                departTime = DateTime.now().setZone('Europe/Ljubljana').toFormat('yyyy-MM-dd\'T\'HH:mm')
            }
        }
        else {
            fromLat = pos?.lat ?? leg?.from_point.lat ?? 46.056946
            fromLon = pos?.lon ?? leg?.from_point.lon ?? 14.505751
            fromTitle = pos ? t('Current location') : (leg?.from_point.name ?? t('Origin'))
            departTime = DateTime.now().setZone('Europe/Ljubljana').toFormat('yyyy-MM-dd\'T\'HH:mm')
        }

        stopNavigation()
        navigate({
            to: '/plan',
            search: {
                from: { title: fromTitle, latitude: fromLat, longitude: fromLon, type: pos ? 'GPS' : 'MAP' },
                to: { title: destination.name, latitude: destination.lat, longitude: destination.lon, type: 'MAP' },
                modes: ['TRANSIT'],
                time: departTime,
                timeMode: 'DEPART',
            } as Record<string, unknown> as any, // eslint-disable-line @typescript-eslint/no-explicit-any
        })
    }

    const handleStop = () => {
        stopSimulation()
        stopNavigation()
        resetAllNotifications()
        navigate({ to: '/plan' } as Record<string, unknown>)
    }

    return (
        <>
            {import.meta.env.DEV && (
                <Box sx={{ p: 1, bgcolor: 'grey.800', display: 'flex', gap: 1 }}>
                    <button onClick={() => startSimulation(10)}>Sim 10x</button>
                    <button onClick={() => stopSimulation()}>Stop Sim</button>
                </Box>
            )}
            <NavigatorLegCard
                leg={currentLeg}
                nextLeg={plan.legs[legIndex + 1]}
                legIndex={legIndex}
                totalLegs={plan.legs.length}
                planStartTime={plan.start_time}
                planEndTime={plan.end_time}
                nextStop={nextStop}
                transferStatus={transferStatus}
                delaySeconds={delaySeconds}
                currentTransfer={currentTransfer}
                nextWalkStep={nextWalkStep}
                onSwitchToAlternative={switchToAlternative}
                onStop={handleStop}
                onReplan={handleReplan}
                onViewPlan={() => router.history.back()}
                onPrevStep={() => updateLegIndex(prev => Math.max(0, prev - 1))}
                onNextStep={() => updateLegIndex(prev => Math.min(plan.legs.length - 1, prev + 1))}
            />
        </>
    )
}
